import type { CostIndexClimber, CostIndexPoint, CostIndexReferenceInflation, CostIndexSkuClimber, Currency } from "./types";

/** Plot height in px: twice the first build's 420 so a Level 3 cost-line view does not read squashed. */
export const MATRIX_HEIGHT = 840;

export type MatrixKind = "skus" | "places" | "lines" | "lanes";
export type MatrixRow = CostIndexSkuClimber | CostIndexClimber | {
  label: string;
  delta_pct: string | number | null;
  impact_window: string | number | null;
  color?: string;
};

export type MatrixPoint<T extends MatrixRow = MatrixRow> = {
  id: string;
  name: string;
  x: number;
  y: number;
  row: T;
  corner: boolean;
};

export type MatrixSplits = { x: number; y: number };
export type MatrixBounds = { left: number; right: number; top: number; bottom: number; width: number; height: number };
export type MatrixLayoutPoint<T extends MatrixRow = MatrixRow> = MatrixPoint<T> & {
  cx: number;
  cy: number;
  labelY: number;
  anchor: "start" | "end";
  labelX: number;
  moved: boolean;
  /** Drawn at the left edge although its impact is below zero (only with `parkFalling`). */
  parked: boolean;
  /** False when `fitLabelText` left the label out because it did not fit. */
  labelShown: boolean;
};
export type MatrixLayout<T extends MatrixRow = MatrixRow> = {
  points: MatrixLayoutPoint<T>[];
  bounds: MatrixBounds;
  xDomain: [number, number];
  yDomain: [number, number];
  xSplit: number;
  ySplit: number;
  xScale: (value: number) => number;
  yScale: (value: number) => number;
};

export type MatrixSummary = {
  plotted: number;
  notPlotted: number;
  cornerCount: number;
  cornerImpact: number;
  cornerNames: string[];
};

/** How the rise line (y split) is drawn: CPI over the window (scope average when the response has none), the scope average, or a typed percent. */
export type RiseRule = { kind: "cpi" } | { kind: "scope" } | { kind: "pct"; value: number };
/** How the impact line (x split) is drawn: the mean impact of the points shown, or a typed amount in the presentation currency it was typed in (never converted). */
export type ImpactRule = { kind: "mean" } | { kind: "amount"; value: number; currency?: Currency };
export type MatrixThresholds = { rise: RiseRule; impact: ImpactRule };
/** The rise rule actually drawn: "cpi" only when the rule asks for CPI and the response carries it; "none" only on an opt-in matrix with no CPI and no scope index (the line sits at 0). */
export type RiseKind = "cpi" | "scope" | "pct" | "none";

export const DEFAULT_MATRIX_THRESHOLDS: MatrixThresholds = { rise: { kind: "cpi" }, impact: { kind: "mean" } };
export const RISE_LINE_PARAM = "rise_line";
export const IMPACT_LINE_PARAM = "impact_line";
export const IMPACT_CCY_PARAM = "impact_ccy";

const RISE_PCT = /^-?\d+(\.\d+)?$/;
const IMPACT_AMOUNT = /^\d+(\.\d+)?$/;
const CURRENCY_CODES: readonly string[] = ["GBP", "EUR", "AED", "USD"] satisfies Currency[];
const searchText = (value: unknown) => typeof value === "string" ? value.trim() : typeof value === "number" && Number.isFinite(value) ? String(value) : "";

/** Two decimals, so a URL never carries float noise such as 0.30000000000000004. */
export const roundThreshold = (value: number) => Math.round(value * 100) / 100;

export function effectiveRiseKind(rule: RiseRule, cpiAvailable: boolean): RiseKind {
  if (rule.kind === "pct") return "pct";
  if (rule.kind === "scope") return "scope";
  return cpiAvailable ? "cpi" : "scope";
}

/** The scope's latest index change, or null when the last month has no finite index (an empty basket). */
export function scopeAverage(series: CostIndexPoint[]): number | null {
  const latest = finite(series.at(-1)?.total_index);
  return latest == null ? null : latest - 100;
}

/** A typed amount, stamped with the currency it was typed in when that is known. */
export const amountRule = (value: number, currency?: Currency | null): ImpactRule => currency ? { kind: "amount", value, currency } : { kind: "amount", value };

/** True when a typed amount was typed in another currency than `currency` (an amount with no currency reads in the view's currency). */
export const amountInOtherCurrency = (impact: ImpactRule, currency?: Currency | null) => impact.kind === "amount" && impact.currency != null && currency != null && impact.currency !== currency;

/**
 * The rule pair actually drawn: a Scope average rule with no scope index reads as the CPI rule, and an amount typed in another
 * currency than the view's reads as the Mean rule. Unchanged input returns the same object.
 */
export function drawnThresholds(thresholds: MatrixThresholds, series: CostIndexPoint[], currency?: Currency | null): MatrixThresholds {
  const rise: RiseRule = thresholds.rise.kind === "scope" && scopeAverage(series) == null ? { kind: "cpi" } : thresholds.rise;
  const impact: ImpactRule = amountInOtherCurrency(thresholds.impact, currency) ? { kind: "mean" } : thresholds.impact;
  return rise === thresholds.rise && impact === thresholds.impact ? thresholds : { rise, impact };
}

/** The pair after pressing a Rise line button. `Set %` keeps the typed value while `Set %` is already the rule; after another rule it seeds the drawn line to 1 decimal. */
export function pickRiseRule(thresholds: MatrixThresholds, kind: RiseRule["kind"], split: MatrixSplits): MatrixThresholds {
  const { rise } = thresholds;
  return { ...thresholds, rise: kind === "pct" ? { kind: "pct", value: rise.kind === "pct" ? rise.value : Math.round(split.y * 10) / 10 } : kind === "scope" ? { kind: "scope" } : { kind: "cpi" } };
}

/**
 * The pair after pressing an Impact line button. `Set amount` keeps the typed value while `Set amount` is already the rule in this
 * currency; otherwise it seeds the drawn line to a whole unit, never below 0. The amount is stamped with `currency`.
 */
export function pickImpactRule(thresholds: MatrixThresholds, kind: ImpactRule["kind"], split: MatrixSplits, currency?: Currency | null): MatrixThresholds {
  const { impact } = thresholds;
  if (kind === "mean") return { ...thresholds, impact: { kind: "mean" } };
  const kept = impact.kind === "amount" && !amountInOtherCurrency(impact, currency) ? impact.value : null;
  return { ...thresholds, impact: amountRule(kept ?? Math.max(0, Math.round(split.x)), currency) };
}

/** A typed box value: null when blank, unreadable or below `min`; else rounded to 2 decimals. */
export function parseThresholdDraft(text: string, min?: number): number | null {
  const parsed = Number(text);
  if (text.trim() === "" || !Number.isFinite(parsed) || min != null && parsed < min) return null;
  return roundThreshold(parsed);
}

/** Read the two lines from URL search. TanStack Router JSON-parses values, so a number may arrive as a number. Anything unreadable is the default rule. */
export function matrixThresholdsFromSearch(search: Record<string, unknown>): MatrixThresholds {
  const rise = searchText(search[RISE_LINE_PARAM]);
  const impact = searchText(search[IMPACT_LINE_PARAM]);
  const ccy = searchText(search[IMPACT_CCY_PARAM]);
  return {
    rise: rise === "scope" ? { kind: "scope" } : RISE_PCT.test(rise) ? { kind: "pct", value: Number(rise) } : { kind: "cpi" },
    impact: IMPACT_AMOUNT.test(impact) ? amountRule(Number(impact), CURRENCY_CODES.includes(ccy) ? ccy as Currency : null) : { kind: "mean" },
  };
}

/** The URL patch for a rule pair. A default rule writes null (the param is removed); numbers stay numbers; an amount carries its currency. */
export function matrixThresholdsSearchPatch(thresholds: MatrixThresholds): { rise_line: "scope" | number | null; impact_line: number | null; impact_ccy: Currency | null } {
  const { rise, impact } = thresholds;
  return {
    rise_line: rise.kind === "scope" ? "scope" : rise.kind === "pct" ? roundThreshold(rise.value) : null,
    impact_line: impact.kind === "amount" ? roundThreshold(Math.max(0, impact.value)) : null,
    impact_ccy: impact.kind === "amount" ? impact.currency ?? null : null,
  };
}

const finite = (value: string | number | null | undefined): number | null => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const rowName = (row: MatrixRow, kind: MatrixKind) => kind === "skus"
  ? [(row as CostIndexSkuClimber).sku, (row as CostIndexSkuClimber & { place_label?: string }).place_label].filter(Boolean).join(" · ")
  : (row as Exclude<MatrixRow, CostIndexSkuClimber>).label;

/** Rows with either missing axis value do not enter any matrix arithmetic. */
export function matrixPoints<T extends MatrixRow>(rows: T[], requestedKind?: MatrixKind): MatrixPoint<T>[] {
  return rows.flatMap((row, index) => {
    const kind = requestedKind ?? ("sku" in row ? "skus" : "lines");
    const x = finite(row.impact_window);
    const y = finite(row.delta_pct);
    if (x == null || y == null) return [];
    const id = kind === "skus"
      ? [(row as CostIndexSkuClimber).sku, (row as CostIndexSkuClimber & { place_id?: string }).place_id].filter(Boolean).join(":")
      : "key" in row && row.key ? row.key : rowName(row, kind) || String(index);
    return [{ id, name: rowName(row, kind), x, y, row, corner: false }];
  });
}

/** The two display-only cuts. Default rule: mean plotted impact, and CPI falling back to the scope's latest index change. */
export function splits(points: MatrixPoint[], series: CostIndexPoint[], inflation?: Pick<CostIndexReferenceInflation, "cumulative_pct"> | null, thresholds: MatrixThresholds = DEFAULT_MATRIX_THRESHOLDS): MatrixSplits {
  const mean = points.length > 0 ? points.reduce((total, point) => total + point.x, 0) / points.length : 0;
  const reference = finite(inflation?.cumulative_pct);
  const scope = scopeAverage(series);
  const { rise, impact } = drawnThresholds(thresholds, series);
  const x = impact.kind === "amount" ? impact.value : mean;
  const y = rise.kind === "pct" ? rise.value : rise.kind === "scope" ? scope ?? 0 : reference ?? scope ?? 0;
  return { x, y };
}

/**
 * Spread labels in screen coordinates. Values are returned in input order.
 * Pass one sorts top-to-bottom and pushes down; pass two clamps the last label
 * and pushes the stack back up, matching the canvas prototype.
 */
export function spread(values: number[], top: number, bottom: number, gap = 14): number[] {
  if (values.length === 0) return [];
  const ordered = values.map((value, index) => ({ value: Math.min(bottom, Math.max(top, value)), index })).sort((a, b) => a.value - b.value);
  for (let index = 1; index < ordered.length; index += 1) ordered[index].value = Math.max(ordered[index].value, ordered[index - 1].value + gap);
  ordered.at(-1)!.value = Math.min(bottom, ordered.at(-1)!.value);
  for (let index = ordered.length - 2; index >= 0; index -= 1) ordered[index].value = Math.min(ordered[index].value, ordered[index + 1].value - gap);
  if (ordered[0].value < top) {
    const shift = top - ordered[0].value;
    for (const item of ordered) item.value += shift;
  }
  return ordered.sort((a, b) => a.index - b.index).map((item) => item.value);
}

const domain = (values: number[], headroom = 0): [number, number] => {
  const low = Math.min(0, ...values);
  const high = Math.max(0, ...values);
  const range = high - low || Math.max(Math.abs(high), Math.abs(low), 1);
  return [low, high + range * headroom];
};

export type MatrixLayoutOptions<T extends MatrixRow = MatrixRow> = {
  /** Draw a point whose impact fell at the left edge (impact 0) instead of stretching the axis below zero; arithmetic is unchanged. */
  parkFalling?: boolean;
  /** Keep each label beside its point and draw it only where it fits, corner points first; the text is what gets measured. */
  fitLabelText?: (point: MatrixPoint<T>) => string;
};

/** Average glyph width and line box of an 11 px matrix label, used to decide whether a fitted label fits. */
export const LABEL_CHAR_WIDTH = 6.2;
export const LABEL_HEIGHT = 13;

type Box = { left: number; right: number; top: number; bottom: number };

/** Greedy placement: corner points first, then by impact, largest first; a label is kept only inside the plot and clear of every kept label. */
export function fittedLabelIds<T extends MatrixRow>(points: MatrixLayoutPoint<T>[], bounds: MatrixBounds, text: (point: MatrixPoint<T>) => string): Set<string> {
  const order = [...points].sort((a, b) => Number(b.corner) - Number(a.corner) || b.x - a.x);
  const kept: Box[] = [];
  const ids = new Set<string>();
  for (const point of order) {
    const width = text(point).length * LABEL_CHAR_WIDTH;
    const left = point.anchor === "end" ? point.labelX - width : point.labelX;
    const box = { left, right: left + width, top: point.labelY - LABEL_HEIGHT / 2, bottom: point.labelY + LABEL_HEIGHT / 2 };
    if (box.left < bounds.left || box.right > bounds.right || box.top < bounds.top || box.bottom > bounds.bottom) continue;
    if (kept.some((other) => box.left < other.right && other.left < box.right && box.top < other.bottom && other.top < box.bottom)) continue;
    kept.push(box);
    ids.add(point.id);
  }
  return ids;
}

/** Convert data points to SVG geometry; all dimensions are deterministic and unit-testable. With no options this is the v1.0 drawing. */
export function layout<T extends MatrixRow>(points: MatrixPoint<T>[], split: MatrixSplits, width = 1240, height = MATRIX_HEIGHT, options: MatrixLayoutOptions<T> = {}): MatrixLayout<T> {
  const bounds: MatrixBounds = { left: 82, right: width - 28, top: 30, bottom: height - 70, width: width - 110, height: height - 100 };
  const drawnX = (value: number) => options.parkFalling ? Math.max(0, value) : value;
  const xDomain = domain([...points.map((point) => drawnX(point.x)), drawnX(split.x)]);
  const yDomain = domain([...points.map((point) => point.y), split.y], 0.08);
  const xRange = xDomain[1] - xDomain[0] || 1;
  const yRange = yDomain[1] - yDomain[0] || 1;
  const xScale = (value: number) => bounds.left + ((value - xDomain[0]) / xRange) * bounds.width;
  const yScale = (value: number) => bounds.bottom - ((value - yDomain[0]) / yRange) * bounds.height;
  const raw = points.map((point) => ({ ...point, corner: point.x >= split.x && point.y > split.y, cx: xScale(drawnX(point.x)), cy: yScale(point.y) }));
  const labelYs = options.fitLabelText ? raw.map((point) => point.cy) : spread(raw.map((point) => point.cy), bounds.top + 7, bounds.bottom - 7);
  const placed = raw.map((point, index) => {
    const anchor = (point.cx - bounds.left) / bounds.width >= 0.72 ? "end" as const : "start" as const;
    return { ...point, labelY: labelYs[index], anchor, labelX: point.cx + (anchor === "end" ? -10 : 10), moved: Math.abs(labelYs[index] - point.cy) > 9, parked: Boolean(options.parkFalling) && point.x < 0, labelShown: true };
  });
  const shown = options.fitLabelText ? fittedLabelIds(placed, bounds, options.fitLabelText) : null;
  return {
    bounds, xDomain, yDomain, xSplit: xScale(drawnX(split.x)), ySplit: yScale(split.y), xScale, yScale,
    points: shown ? placed.map((point) => ({ ...point, labelShown: shown.has(point.id) })) : placed,
  };
}

export function summary(rows: MatrixRow[], points: MatrixPoint[], split: MatrixSplits): MatrixSummary {
  const corner = points.filter((point) => point.x >= split.x && point.y > split.y).sort((a, b) => b.x - a.x);
  return {
    plotted: points.length,
    notPlotted: rows.length - points.length,
    cornerCount: corner.length,
    cornerImpact: corner.reduce((total, point) => total + point.x, 0),
    cornerNames: corner.map((point) => point.name),
  };
}

/** Plotted points whose impact fell below zero: the ones `parkFalling` draws at the left edge. */
export const fellCount = (points: MatrixPoint[]) => points.filter((point) => point.x < 0).length;
